import { createSlice } from "@reduxjs/toolkit";
import { getCmmInputDataForOrder } from "../../../api/bigQuery/nycbsPharmaOrders";
import { mapToCoverMyMedsInputModel } from "../../../data-model/cmmInputRequestModel";
import { NycbsPharmaOrderModel } from "../../../data-model/nycbsPharmaOrder";
import { NycbsPharmaOrderKeys } from "../../../enums/nycbsPharmaOrder";
import {
  persistCmmOrderAssigneeId,
  readCmmOrderAssigneeId,
} from "../../../utils/cmmOrderAssigneeIdStorage";
import { AppDispatch } from "../../store/store";

interface CmmSingleOrderModel {
  data?: NycbsPharmaOrderModel;
  loading: boolean;
  error: string | null;
}

const initialState: CmmSingleOrderModel = {
  data: undefined,
  loading: false,
  error: null,
};

/**
 * Reads a non-empty `assignee_id` off an order-shaped object.
 *
 * @param order - Order payload from either the list or single-order endpoint
 * @returns Trimmed assignee UID, or null when absent or empty
 */
const getAssigneeId = (
  order: Record<string, unknown> | null | undefined,
): string | null => {
  const value = order?.[NycbsPharmaOrderKeys.AssigneeId];
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Resolves an order's assignee UID, working around the single-order endpoint
 * returning `assignee_id` empty while list endpoints populate it.
 *
 * Sources are tried in order of freshness: the response itself, the already
 * loaded orders list, then the cache written by earlier list loads.
 *
 * @param orderId - Order identifier being fetched
 * @param details - Raw single-order API details payload
 * @param listOrders - Orders currently held by the CMM orders list slice
 * @returns Assignee UID, or null when no source has one
 */
const resolveAssigneeId = (
  orderId: string,
  details: Record<string, unknown> | null | undefined,
  listOrders: Record<string, unknown>[] | null | undefined,
): string | null =>
  getAssigneeId(details) ??
  getAssigneeId(
    listOrders?.find((order) => order?.identifier === orderId) ?? null,
  ) ??
  readCmmOrderAssigneeId(orderId);

const cmmSingleOrderSlice = createSlice({
  name: "cmmSingleOrderSlice",
  initialState,
  reducers: {
    fetchCmmSingleOrderStart(state) {
      state.loading = true;
      state.error = null;
      state.data = undefined;
    },
    fetchCmmSingleOrderSuccess(state, action) {
      state.data = action.payload;
      state.loading = false;
    },
    fetchCmmSingleOrderFailure(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    resetCmmSingleOrder(state) {
      state.data = undefined;
      state.loading = false;
      state.error = null;
    },
  },
});

export const { resetCmmSingleOrder } = cmmSingleOrderSlice.actions;

export const fetchSingleOrderDetailsForCmm =
  (orderId: string) => async (dispatch: AppDispatch, getState) => {
    const { cmmSingleOrder } = getState();
    if (cmmSingleOrder.loading) return;

    dispatch(cmmSingleOrderSlice.actions.fetchCmmSingleOrderStart());
    try {
      const results = await getCmmInputDataForOrder(orderId);
      const details = results["details"];

      const assigneeId = resolveAssigneeId(
        orderId,
        details,
        getState().nycbsPharmaOrders?.data,
      );
      persistCmmOrderAssigneeId(orderId, assigneeId);

      const ordersData =
        details && assigneeId
          ? { ...details, [NycbsPharmaOrderKeys.AssigneeId]: assigneeId }
          : details;

      dispatch(
        cmmSingleOrderSlice.actions.fetchCmmSingleOrderSuccess(
          mapToCoverMyMedsInputModel(ordersData),
        ),
      );
    } catch (error) {
      dispatch(
        cmmSingleOrderSlice.actions.fetchCmmSingleOrderFailure(
          "Error fetching orders",
        ),
      );
    }
  };

export const updateCmmSingleOrder =
  (data: NycbsPharmaOrderModel) => async (dispatch: AppDispatch, getState) => {
    const { cmmSingleOrder } = getState();
    if (cmmSingleOrder.loading) return;

    const updatedData = mapToCoverMyMedsInputModel(data);

    dispatch(
      cmmSingleOrderSlice.actions.fetchCmmSingleOrderSuccess(updatedData),
    );
  };

export const resetSingleOrderData = () => async (dispatch: AppDispatch) => {
  dispatch(cmmSingleOrderSlice.actions.resetCmmSingleOrder());
};

export default cmmSingleOrderSlice.reducer;
